import React, { useEffect, useState, useRef, useMemo } from "react";
import { useAppSelector, useAppDispatch } from "hooks/redux";
import { useTranslation } from "react-i18next";
import useModal from "hooks/modals/useModal";
import axios, { CancelToken, CancelTokenSource } from "axios";
import { chunk, cloneDeep, groupBy, range, take } from "lodash";

import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import {
  GetTeeTime,
  GetBookingCategory,
  DeleteTeeTime,
  ImportDrawsheet,
  PostTeeTimeSqueeze,
  GetTeeTimeCartsAvailable,
  IGetTeeTimeCartsAvailableResponse,
  AddTournamentPlayer,
} from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";
import {
  PutCancelBooking,
  PutCancelParticipant,
  PutBookingParticipantNoShow,
  GetBooking,
  PutMoveBooking,
  PutCheckAllParticipant,
  PutBookingConfirmation,
  PutBookingParticipant,
} from "api/rpc/2024-04/facilityAdmin/teesheet/booking";
import { PostTeeTimeLock } from "api/rpc/facilityAdmin/teesheet/teetime";
import { GetShotGunTemplate } from "api/rpc/2024-04/facilityAdmin/teesheet/template";
import { RemoveShotgunTemplate } from "api/rpc/2024-04/facilityAdmin/teesheet/shotgun";
import { IAPIResponse, StatusCode } from "api/protocols";

import { selectTeeTimeSuccess, selectTeeTime } from "redux/actions/teesheet";
import { showError, showSuccess, initiateNewBooking, initiateEditBooking, enqueue, dequeue } from "redux/actions/ui";

import { valueToString, calculateHexColorBrightness } from "helpers/Helpers";

import Spin from "components/spin/spin";
import Portal from "elements/Portal";
import Popup from "components/popup/Popup";
import BookingModalNew from "./modals/BookingModalNew";
import BookingModalEdit from "./modals/BookingModalEdit";
import { Select } from "components/select/index";
import CreateShotgunModal from "./modals/CreateShotgunModal";
import AuditTrailModal from "./modals/AuditTrailModal";
import TeeTimeDetailsModal from "./modals/TeeTimeDetailsModal";
import AddTeeTimeModal from "./modals/AddTeeTimeModal";
import EditStartHoleModal from "./modals/EditStartHoleModal";
import ImportDrawSheetModal from "./modals/ImportDrawSheetModal";
import Callout from "components/callout/Callout";

import { IDivision } from "redux/reducers/models/facility";
import { ILeague } from "redux/reducers/models/league";
import { ITournament } from "redux/reducers/models/tournament";
import {
  ITeeTimeNew,
  ITeeTimeSlotNew,
  IBookingCategory,
  ITeeTimeBookingCategory,
  IBookingParticipant,
  ITeeTime,
  IBooking,
} from "redux/reducers/models/teetime";
import { TShotgunTemplate } from "redux/reducers/models/teesheet";

import { DragDropContext, DragStart, DropResult } from "react-beautiful-dnd";

import "./newTeeSheetTabs.scss";
import Checkbox from "components/form/checkbox/Checkbox";
import Sheet from "components/sheet/Sheet";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { IProduct } from "redux/reducers/models/product";
import { LocaleCurrency } from "helpers/Locale";
import useCancelToken from "hooks/useCancelToken/useCancelToken";
import { PostLineItemsToOrder, PostOrder } from "api/rpc/2024-04/facilityAdmin/order/order";
import { PostTransaction, PutCapture } from "api/rpc/2024-04/facilityAdmin/order/transaction";
import { GetCustomerPaymentMethod } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { useStripe } from "@stripe/react-stripe-js";
import {
  loadingTeetimeTabActionsAreEqual,
  loadingTeetimeTabActionsContainsId,
  TLoadingTeetimeTabsActionStatus,
  TLoadingAddBookingsAction,
  TNewTeeSheetLoadingTeetimeTabAction,
  TLoadingMoveBookingsAction,
  TLoadingUpdateBookingAction,
} from "./loadingTeetimeTabActions";
import { TeesheetActionTypes } from "redux/actions/actionTypes";
import { ISideBySideTeeSheetDivision, ITeeTimeTabNew } from "./NewTeeSheet";
import NewTeeSheetTabsDivision, { NewTeeSheetTabsDivisionUpdateModalType } from "./NewTeeSheetTabsDivision";
import { TTeeSheetView } from "hooks/useTeeSheetSideBySideSettings/useTeeSheetSideBySideSettings";
import Toggle from "components/form/toggle/Toggle";
import { GetEventScorecards } from "api/rpc/2024-04/facilityAdmin/event/scorecard";
import AddTournamentPlayerModal from "./modals/AddTournamentPlayer";
import { ICustomer } from "redux/reducers/models/customer";

interface INewTeeSheetTabs {
  teeSheetView: TTeeSheetView;
  sideBySideTeeSheetDivisions: ISideBySideTeeSheetDivision[];
  allDivisions: IDivision[];
  teetimeTabs: ITeeTimeTabNew[];
  teetimeBookingCategories: ITeeTimeBookingCategory[][];
  reloadTeeTimeTabs: () => void;
  reloadActionTeeTimeTabs: (
    loadingTeetimeTabAction: TNewTeeSheetLoadingTeetimeTabAction,
    useLoader: boolean,
  ) => Promise<TLoadingTeetimeTabsActionStatus>;
  debouncedTeeTimeSearch: string;
  //Manually set the tee times in state
  setTeeTimes?: (teeTimes: Array<ITeeTimeNew>) => void;
  loadingTeetimeTabActions: TNewTeeSheetLoadingTeetimeTabAction[];
  setLoadingTeetimeTabActions: React.Dispatch<React.SetStateAction<TNewTeeSheetLoadingTeetimeTabAction[]>>;
}

export interface INewTeeSheetTabsState {
  sortedTeeTimes: ITeeTimeTabNew[];
  groupedTeeTimes: ITeeTimeDraggable[];
  selectedTeeTime: ITeeTimeTabNew;
  teeSheetLeagues: ILeague[];
  teeSheetTournaments: ITournament[];
}

type TGroupedSideBySideTeeSheetDivision = ISideBySideTeeSheetDivision & {
  sortedTeeTimes: ITeeTimeTabNew[];
  groupedTeeTimes: ITeeTimeDraggable[];
};

interface IDragAndDropMoveGroup {
  sourceTeeTime: ITeeTimeDraggable;
  destTeeTime: ITeeTimeDraggable;
}

export interface INewTeeSheetTabsDragAndDropState {
  teetimeToolTipDisabled: boolean;
  foundSourceTeeTime: ITeeTimeDraggable;
  draggingSlotGroup: ITeeTimeSlotNew[];
  moveGroups: IDragAndDropMoveGroup[];
}

type TDraggableSlot = Array<Array<ITeeTimeSlotNew>>;

export interface ITeeTimeDraggable extends Omit<ITeeTimeTabNew, "slots"> {
  slots: TDraggableSlot;
}

interface IMoveTeetimeConfirmationModal {
  teeTimeBookingID: number;
  startTeeTimeID: number;
  destinationTeeTimeID: number;
  displayHoleConversionWarning: boolean;
  draggingBookingHoles: number;
  sendConfirmation: boolean;
}

export interface ICheckInAllShotGunParticipantsModal {
  teetime: ITeeTimeTabNew;
}

export interface ICheckInAllTournamentParticipantsModal {
  teetime: ITeeTimeTabNew;
}

export interface ICheckInAllLeagueParticipantsModal {
  teetime: ITeeTimeTabNew;
}

export interface INoShowModal {
  type: string;
  bookingParticipantIds: number[];
  slotGroup: ITeeTimeSlotNew[];
  teeTimeIndex: number;
  chargeNoShow: boolean;
  bookingId: number;
}

interface INoShowChargeModal {
  booking: IBooking;
  selectedProduct: any;
  noShowProducts: IProduct[];
  paymentMethod: {
    stripe_payment_method_id: string;
    last4: string;
  };
  confirmationPopup: boolean;
}

export interface ICreateShotgunModal {
  teetime: ITeeTimeTabNew;
}

export interface IAuditTrailModal {
  teetime: ITeeTimeTabNew;
  bookingId: number;
  type: "booking" | "teeTime";
}

export interface ITeeTimeDetailsModal {
  teetime: ITeeTimeTabNew;
}

export interface IEditStartHoleModal {
  teetime: ITeeTimeTabNew;
  start_hole: string;
}

export interface IAddTeeTimeModal {
  teetime: ITeeTimeTabNew;
}

export interface IAddSqueezeTimeModal {
  teetime: ITeeTimeTabNew;
}

export interface IFileImportModal {
  shotgun_application_id: number;
}

export interface IAddTournamentPlayerModal {
  slot: ITeeTimeSlotNew;
  organizer: ICustomer;
}

export interface IRemoveShotgunPopup {
  teetime: ITeeTimeTabNew;
}

export interface ICancelBookingPopup {
  teetimeToCancelDate: string;
  teetimeToCancelTime: string;
  teetimeToCancelBookingId: number;
  send_cancellation_email: boolean;
}

export interface ICancelPlayerPopup {
  bookingParticipantName: string;
  bookingParticipantId: number;
}

export interface IRemoveTeeTimePopup {
  teetime: ITeeTimeTabNew;
}

export function calculateDivisionColors(backgroundHexColor: string): { backgroundColor: string; color: string } {
  const backgroundColorBrightness = calculateHexColorBrightness(backgroundHexColor);

  if (backgroundColorBrightness) {
    return {
      backgroundColor: backgroundHexColor,
      color: backgroundColorBrightness < 50 ? "white" : "black",
    };
  } else {
    return {
      backgroundColor: "#e9e9e9",
      color: "#999999",
    };
  }
}

export default function NewTeeSheetTabs(props: INewTeeSheetTabs) {
  {
    /* CONSTANTS */
  }
  const { facilityStore } = useAppSelector(store => store);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { cancelToken, cancelRequest } = useCancelToken();
  const stripe = useStripe();

  const {
    teeSheetView,
    sideBySideTeeSheetDivisions,
    allDivisions,
    setTeeTimes,
    loadingTeetimeTabActions,
    setLoadingTeetimeTabActions,
  } = props;

  const [bookingCategories, setBookingCategories] = useState<IBookingCategory[]>(undefined);
  const [shotgunTemplates, setShotgunTemplates] = useState<TShotgunTemplate[]>(undefined);
  const [state, setState] = useState<INewTeeSheetTabsState>({
    sortedTeeTimes: null,
    groupedTeeTimes: null,
    selectedTeeTime: null,
    teeSheetLeagues: undefined,
    teeSheetTournaments: undefined,
  });

  const [dragAndDropState, setDragAndDropState] = useState<INewTeeSheetTabsDragAndDropState>({
    teetimeToolTipDisabled: false,
    draggingSlotGroup: undefined,
    foundSourceTeeTime: undefined,
    moveGroups: [],
  });

  {
    /* MODALS */
  }
  const { state: newBookingModal, updateModal: updateNewBookingModal } = useModal({
    clearOnClose: true,
    loading: false,
    loadingText: "",
    postingLock: false,
    slot: null,
    cartsAvailable: {
      nineHole: 0,
      eighteenHole: 0,
    },
  });

  const {
    state: noShowModal,
    updateModal: updateNoShowModal,
    closeModal: closeNoShowModal,
  } = useModal<INoShowModal>({
    clearOnClose: true,
    type: "charge",
    bookingParticipantIds: [],
    slotGroup: null,
    teeTimeIndex: null,
    chargeNoShow: false,
    bookingId: null,
  });

  const {
    state: noShowChargeModal,
    updateModal: updateNoShowChargeModal,
    closeModal: closeNoShowChargeModal,
  } = useModal<INoShowChargeModal>({
    clearOnClose: true,
    booking: null,
    selectedProduct: null,
    noShowProducts: null,
    paymentMethod: null,
    confirmationPopup: false,
    sendNoShowChargeEmail: false,
  });

  const {
    state: createShotgunModal,
    updateModal: updateCreateShotgunModal,
    closeModal: closeCreateShotgunModal,
  } = useModal<ICreateShotgunModal>({
    clearOnClose: true,
    teetime: null,
  });

  const { state: auditTrailModal, updateModal: updateAuditTrailModal } = useModal<IAuditTrailModal>({
    clearOnClose: true,
    teetime: null,
    bookingId: null,
    type: null,
  });

  const {
    state: teeTimeDetailsModal,
    updateModal: updateTeeTimeDetailsModal,
    closeModal: closeTeeTimeDetailsModal,
  } = useModal<ITeeTimeDetailsModal>({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: editStartHoleModal,
    updateModal: updateEditStartHoleModal,
    closeModal: closeEditStartHoleModal,
  } = useModal<IEditStartHoleModal>({
    clearOnClose: true,
    teetime: null,
    start_hole: "",
  });

  const {
    state: addTeeTimeModal,
    updateModal: updateAddTeeTimeModal,
    closeModal: closeAddTeeTimeModal,
  } = useModal<IAddTeeTimeModal>({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: addSqueezeTimeModal,
    updateModal: updateAddSqueezeTimeModal,
    closeModal: closeAddSqueezeTimeModal,
  } = useModal<IAddSqueezeTimeModal>({
    clearOnClose: true,
    teetime: null,
  });

  const { state: editBookingModal, updateModal: updateEditBookingModal } = useModal({
    clearOnClose: true,
    selectedTeeTime: null,
    selectedBooking: null,
    teeTimeLock: false,
    loading: false,
    loadingText: "",
    postingLock: false,
    cartsAvailable: {
      nineHole: 0,
      eighteenHole: 0,
    },
  });

  const {
    state: fileImportModal,
    updateModal: updateFileImportModal,
    closeModal: closeFileImportModal,
  } = useModal<IFileImportModal>({ shotgun_application_id: null });

  const {
    state: addTournamentPlayerModal,
    updateModal: updateAddTournamentPlayer,
    closeModal: closeAddTournamentPlayer,
  } = useModal<IAddTournamentPlayerModal>({ slot: null, organizer: null });

  {
    /* POPUPS */
  }

  const {
    state: noCreditCardPopup,
    updateModal: updateNoCreditCardPopup,
    closeModal: closeNoCreditCardPopup,
  } = useModal({
    clearOnClose: true,
  });

  const {
    state: blockedTeeTimePopup,
    updateModal: updateBlockedTeeTimePopup,
    closeModal: closeBlockedTeeTimePopup,
  } = useModal();

  const {
    state: selectDivisionsPopup,
    updateModal: updateSelectDivisionsPopup,
    closeModal: closeSelectDivisionsPopup,
  } = useModal({
    divisions: facilityStore?.facility?.courses[0]?.divisions,
    selectedFrontNineDivision: null,
    selectedBackNineDivision: null,
    teetime: null,
  });

  const { state: cancelBookingPopup, updateModal: updateCancelBookingPopup } = useModal<ICancelBookingPopup>({
    clearOnClose: true,
    teetimeToCancelDate: "",
    teetimeToCancelTime: "",
    teetimeToCancelBookingId: null,
    send_cancellation_email: true,
  });

  const { state: cancelPlayerPopup, updateModal: updateCancelPlayerPopup } = useModal<ICancelPlayerPopup>({
    clearOnClose: true,
    bookingParticipantName: "",
    bookingParticipantId: null,
  });

  const {
    state: teeTimeBookedPopup,
    updateModal: updateTeeTimeBookedPopup,
    closeModal: closeTeeTimeBookedPopup,
  } = useModal();

  const {
    state: moveTeeTimeBookingConfirmation,
    closeModal: closeMoveTeeTimeBookingConfirmationPopup,
    updateModal: updateMoveTeeTimeBookingConfirmationPopup,
  } = useModal<IMoveTeetimeConfirmationModal>({
    clearOnClose: true,
    teeTimeBookingID: null,
    startTeeTimeID: null,
    destinationTeeTimeID: null,
    displayHoleConversionWarning: false,
    draggingBookingHoles: null,
    sendConfirmation: false,
  });

  const {
    state: teeTimeLockedPopup,
    updateModal: updateTeeTimeLockedPopup,
    closeModal: closeTeeTimeLockedPopup,
  } = useModal();

  const {
    state: removeTeeTimePopup,
    updateModal: updateRemoveTeeTimePopup,
    closeModal: closeRemoveTeeTimePopup,
  } = useModal<IRemoveTeeTimePopup>({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: removeShotgunPopup,
    updateModal: updateRemoveShotgunPopup,
    closeModal: closeRemoveShotgunPopup,
  } = useModal<IRemoveShotgunPopup>({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: shotgunCheckInParticipants,
    updateModal: updateCheckInAllShotgunParticipants,
    closeModal: closeCheckInAllShotgunParticipants,
  } = useModal<ICheckInAllShotGunParticipantsModal>({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: tournamentCheckInParticipants,
    updateModal: updateCheckInAllTournamentParticipants,
    closeModal: closeCheckInAllTournamentParticipants,
  } = useModal<ICheckInAllTournamentParticipantsModal>({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: leagueCheckInParticipants,
    updateModal: updateCheckInAllLeagueParticipants,
    closeModal: closeCheckInAllLeagueParticipants,
  } = useModal<ICheckInAllLeagueParticipantsModal>({
    clearOnClose: true,
    teetime: null,
  });

  function updateModalState<T>(
    newState: Partial<T> & { isOpen?: boolean },
    type: NewTeeSheetTabsDivisionUpdateModalType,
  ) {
    switch (type) {
      case NewTeeSheetTabsDivisionUpdateModalType.NoShow:
        updateNoShowModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.CreateShotgun:
        updateCreateShotgunModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.AuditTrail:
        updateAuditTrailModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.TeeTimeDetails:
        updateTeeTimeDetailsModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.EditStartHole:
        updateEditStartHoleModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.AddTeeTime:
        updateAddTeeTimeModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.AddSqueezeTime:
        updateAddSqueezeTimeModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.FileImport:
        updateFileImportModal(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.RemoveShotgun:
        updateRemoveShotgunPopup(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.CheckInAllLeagueParticipants:
        updateCheckInAllLeagueParticipants(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.CheckInAllTournamentParticipants:
        updateCheckInAllTournamentParticipants(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.CheckInAllShotgunParticipants:
        updateCheckInAllShotgunParticipants(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.CancelBooking:
        updateCancelBookingPopup(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.CancelPlayer:
        updateCancelPlayerPopup(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.BlockedTeeTime:
        updateBlockedTeeTimePopup(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.SelectDivisions:
        updateSelectDivisionsPopup(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.RemoveTeeTime:
        updateRemoveTeeTimePopup(newState);
        break;
      case NewTeeSheetTabsDivisionUpdateModalType.AddTournamentPlayer:
        updateAddTournamentPlayer(newState);
        break;
    }
  }

  {
    /* USE EFFECTS */
  }

  const teeSheetTabsExitCancelSource = useRef<CancelTokenSource>(axios.CancelToken.source());

  useEffect(() => {
    teeSheetTabsExitCancelSource.current = axios.CancelToken.source();

    return () => teeSheetTabsExitCancelSource.current.cancel();
  }, []);

  function groupSlots(slots: ITeeTimeSlotNew[]) {
    const bookedSlots = slots.filter(slot => slot.tee_time_booking_id != null);
    const nonBookedSlots = slots.filter(slot => slot.tee_time_booking_id == null);

    const groupedBookedSlots = Object.values(groupBy(bookedSlots, s => s.tee_time_booking_id));
    const groupedNonBookedSlots = Object.values(groupBy(nonBookedSlots, s => s.tee_time_slot_id));

    const groupedSlots = [...groupedBookedSlots, ...groupedNonBookedSlots];

    return groupedSlots;
  }

  function getSortedAndGroupedTeeTimes(
    teetimeTabs: ITeeTimeTabNew[],
    teeTimeSearch: string,
  ): { sortedTeeTimes: ITeeTimeTabNew[]; groupedTeeTimes: ITeeTimeDraggable[] } {
    if (teetimeTabs == null || teetimeTabs.length === 0) {
      return { sortedTeeTimes: [], groupedTeeTimes: [] };
    }

    let sortedTeeTimes = cloneDeep(teetimeTabs).filter(
      (teetime: ITeeTimeTabNew) => teetime.slots[0].blocked_type !== "shotgun_disabled",
    );

    if (teeTimeSearch !== "") {
      sortedTeeTimes = sortedTeeTimes.filter((teetime: ITeeTimeTabNew) =>
        teetime.slots?.some(slot =>
          `${slot.customer_first_name} ${slot.customer_last_name}`
            .toLocaleLowerCase()
            .includes(teeTimeSearch?.toLocaleLowerCase()),
        ),
      );
    }

    sortedTeeTimes.sort((current, next) => {
      const localCompare = current?.slots[0]?.start_time.localeCompare(next?.slots[0]?.start_time);

      if (localCompare === 0) {
        return current?.slots[0]?.division_id - next?.slots[0]?.division_id;
      } else {
        return localCompare;
      }
    });

    const clonedSortedTeeTimes = cloneDeep(sortedTeeTimes);

    const groupedTeeTimes: ITeeTimeDraggable[] = clonedSortedTeeTimes.map(teetime => {
      return {
        ...teetime,
        slots: groupSlots(teetime.slots),
      };
    });

    return { sortedTeeTimes, groupedTeeTimes };
  }

  const groupedSideBySideDivisions: TGroupedSideBySideTeeSheetDivision[] = useMemo(() => {
    if (teeSheetView === "side-by-side" && sideBySideTeeSheetDivisions?.length > 0) {
      return sideBySideTeeSheetDivisions.map(division => {
        return {
          ...division,
          ...getSortedAndGroupedTeeTimes(division.teetimeTabs, props.debouncedTeeTimeSearch),
        };
      });
    } else {
      return null;
    }
  }, [teeSheetView, sideBySideTeeSheetDivisions, props.debouncedTeeTimeSearch]);

  // Group slots together by booking id
  useEffect(() => {
    if (!props.teetimeTabs) {
      return;
    }

    const { sortedTeeTimes, groupedTeeTimes } = getSortedAndGroupedTeeTimes(
      props.teetimeTabs,
      props.debouncedTeeTimeSearch,
    );

    updateMainState({ sortedTeeTimes, groupedTeeTimes });
  }, [props.teetimeTabs, props.debouncedTeeTimeSearch]);

  // Load events when tee times are updated to display information about them on the tee sheet
  useEffect(() => {
    const source = axios.CancelToken.source();

    if (!props.teetimeTabs) {
      return;
    }

    const teeTimes: ITeeTimeTabNew[] = [...props.teetimeTabs];

    // Display loader
    updateMainState({ teeSheetTournaments: undefined, teeSheetLeagues: undefined });
    // Load tee sheet tournaments and leagues if they exist
    const tournamentIds = teeTimes
      ?.filter(filteredTeeTime => filteredTeeTime?.slots[0].tournament_id)
      ?.map(teetime => {
        if (teetime.slots[0].tournament_id) {
          return teetime.slots[0].tournament_id;
        }
      });
    void loadTeeSheetTournaments([...new Set(tournamentIds)], source.token);

    const leagueIds = teeTimes
      ?.filter(filteredTeeTime => filteredTeeTime?.slots[0].league_id)
      ?.map(teetime => {
        if (teetime.slots[0].league_id) {
          return teetime.slots[0].league_id;
        }
      });
    void loadTeeSheetLeagues([...new Set(leagueIds)], source.token);

    return () => source.cancel();
  }, [props.teetimeTabs]);

  // Load shotgun information
  useEffect(() => {
    const source = axios.CancelToken.source();
    if (!bookingCategories && createShotgunModal.isOpen) {
      void loadBookingCategories(source.token);
    }
    if (!shotgunTemplates && createShotgunModal.isOpen) {
      void loadShotgunTemplates(source.token);
    }
    return () => source.cancel();
  }, [createShotgunModal.isOpen]);

  // Load booking categories for tee time details if they're not already loaded
  useEffect(() => {
    const source = axios.CancelToken.source();
    if (!bookingCategories && teeTimeDetailsModal.isOpen) {
      void loadBookingCategories(source.token);
    }
    return () => source.cancel();
  }, [teeTimeDetailsModal.isOpen]);

  // FUNCTIONS
  function updateMainState(newMainState: Partial<INewTeeSheetTabsState>) {
    setState((cur: INewTeeSheetTabsState) => {
      return { ...cur, ...newMainState };
    });
  }

  async function loadBookingCategories(token?: CancelToken) {
    const bookingCategoryRes = await GetBookingCategory(null, false, token);

    if (token && token.reason) {
      return;
    }

    if (Number(bookingCategoryRes.status) !== Number(StatusCode.OK)) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.138")));
      return;
    }

    setBookingCategories(bookingCategoryRes.data);
  }

  async function loadShotgunTemplates(token?: CancelToken) {
    const templateRes = await GetShotGunTemplate(null, false, token);

    if (token && token.reason) {
      return;
    }
    if (templateRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.139")));
    }

    setShotgunTemplates(templateRes.data);
  }

  // TEE TIME FUNCTIONS
  async function removeTeeTime() {
    const params = {
      tee_time_id: removeTeeTimePopup.teetime.slots[0].tee_time_id,
    };

    const removeRes = await DeleteTeeTime(params, true);

    if (Number(removeRes.status) !== Number(StatusCode.OK)) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.140")));
      return;
    }

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.141")));

    closeRemoveTeeTimePopup();

    props.reloadTeeTimeTabs();
  }

  // async function setBlockedType(teetime: any, type: string) {
  //   teetime.blocked_type = type;

  //   const teeTimeRes = await PutTeeTime({ tee_times: [teetime] }, true);

  //   if (teeTimeRes.status !== StatusCode.OK) {
  //     dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.062")));
  //     return;
  //   }
  // }

  async function createSqueezeTime() {
    const teeTimeRes = await PostTeeTimeSqueeze(
      { tee_time_id: addSqueezeTimeModal.teetime?.slots[0]?.tee_time_id },
      true,
    );

    if (Number(teeTimeRes.status) !== Number(StatusCode.OK)) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.142")));
      return;
    }

    props.reloadTeeTimeTabs();
    closeAddSqueezeTimeModal();
  }

  // BOOKING FUNCTIONS
  async function handleInitiateBooking(teetime: ITeeTimeTabNew, slot?: ITeeTimeSlotNew) {
    // Show booking modal
    dispatch(initiateNewBooking());

    // If tee time is not locked then load it
    updateNewBookingModal({
      loading: true,
      loadingText: t("elements.tee_sheet.tee_sheet_tabs.143"),
      slot: slot !== undefined ? slot : null,
      cartsAvailable: {
        nineHole: 0,
        eighteenHole: 0,
      },
    });

    const cartsAvailablePromise: Promise<IAPIResponse<IGetTeeTimeCartsAvailableResponse>> = facilityStore
      .teeSheetSettings?.validate_power_cart_count
      ? GetTeeTimeCartsAvailable(
          {
            tee_time_id: teetime.slots[0].tee_time_id,
          },
          false,
        )
      : Promise.resolve({ status: StatusCode.OK, data: null, message: null });

    const [teetimeRes, cartsAvailableRes] = await Promise.all([
      GetTeeTime(
        {
          id: teetime.slots[0].tee_time_id,
          extended: true,
          "extended-bookings": true,
          locks: true,
          turn_tee_time: true,
        },
        false,
      ),
      cartsAvailablePromise,
    ]);

    if (teetimeRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.218")));
      return;
    }

    let cartsAvailable9 = 0;
    let cartsAvailable18 = 0;

    if (facilityStore.teeSheetSettings?.validate_power_cart_count) {
      if (cartsAvailableRes.status !== StatusCode.OK) {
        dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.219")));
        return;
      }

      cartsAvailable9 = cartsAvailableRes.data?.carts_available_9 ?? 0;
      cartsAvailable18 = cartsAvailableRes.data?.carts_available_18 ?? 0;
    }

    const updatedTeeTime = teetimeRes.data[0];
    dispatch(selectTeeTimeSuccess(updatedTeeTime));

    if (updatedTeeTime.quantity_remaining === 0) {
      updateTeeTimeBookedPopup({ isOpen: true });
      return;
    }

    // Lock the tee time
    updateNewBookingModal({
      loadingText: t("elements.tee_sheet.tee_sheet_tabs.144"),
      postingLock: true,
    });

    const postLockRes = await PostTeeTimeLock({ tee_time_id: updatedTeeTime.id }, false);

    // Tee time is locked by another user
    if (postLockRes.status !== StatusCode.OK) {
      updateTeeTimeLockedPopup({ isOpen: true });
      updateNewBookingModal({ loading: false, loadingText: "", postingLock: false });
      return;
    }

    updateMainState({ selectedTeeTime: null });
    updateNewBookingModal({
      loading: false,
      loadingText: "",
      postingLock: false,
      cartsAvailable: {
        nineHole: cartsAvailable9,
        eighteenHole: cartsAvailable18,
      },
    });
  }

  async function handleEditBooking(teetime: ITeeTimeTabNew, bookingId: number) {
    // Show edit booking modal while data is loading
    dispatch(initiateEditBooking());

    // Load booking
    updateEditBookingModal({
      isOpen: true,
      loading: true,
      loadingText: t("elements.tee_sheet.tee_sheet_tabs.145"),
      cartsAvailable: {
        nineHole: 0,
        eighteenHole: 0,
      },
    });

    const bookingRes = await GetBooking({ id: bookingId }, false);

    //Check if selected booking has been cancelled by another user
    if (bookingRes.status !== StatusCode.OK || bookingRes.data[0]?.status === "cancelled") {
      props.reloadTeeTimeTabs();
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.063")));
      return;
    }

    const cartsAvailablePromise: Promise<IAPIResponse<IGetTeeTimeCartsAvailableResponse>> = facilityStore
      .teeSheetSettings?.validate_power_cart_count
      ? GetTeeTimeCartsAvailable(
          {
            tee_time_id: teetime.slots[0].tee_time_id,
          },
          false,
        )
      : Promise.resolve({ status: StatusCode.OK, data: null, message: null });

    const [selectTeeTimeAction, cartAvailableRes] = await Promise.all([
      dispatch(selectTeeTime(teetime.slots[0].tee_time_id, true, false)),
      cartsAvailablePromise,
    ]);

    if (selectTeeTimeAction.type === TeesheetActionTypes.UPDATE_SELECTED_TEETIME_ERROR_FACILITY) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.216")));
      return;
    }

    let cartsAvailable9 = 0;
    let cartsAvailable18 = 0;

    if (facilityStore.teeSheetSettings?.validate_power_cart_count) {
      if (cartAvailableRes.status !== StatusCode.OK) {
        dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.217")));
        return;
      }

      cartsAvailable9 = cartAvailableRes.data?.carts_available_9 ?? 0;
      cartsAvailable18 = cartAvailableRes.data?.carts_available_18 ?? 0;
    }

    updateEditBookingModal({
      loadingText: t("elements.tee_sheet.tee_sheet_tabs.146"),
      postingLock: true,
    });

    const postLockRes = await PostTeeTimeLock({ tee_time_id: teetime.slots[0].tee_time_id }, false);

    if (postLockRes.status !== StatusCode.OK) {
      return;
    }

    // Tee time is locked by another user
    if (postLockRes.status !== StatusCode.OK) {
      updateEditBookingModal({
        selectedBooking: null,
        selectedTeeTime: null,
        loading: false,
        postingLock: false,
        teeTimeLock: false,
      });
      dispatch(showError(t("secure.facility.tee_sheet.tee_sheet_booking_details.005")));
    }

    updateEditBookingModal({
      selectedBooking: bookingRes.data[0],
      selectedTeeTime: teetime,
      cartsAvailable: {
        nineHole: cartsAvailable9,
        eighteenHole: cartsAvailable18,
      },
      loading: false,
      postingLock: false,
    });
  }

  async function cancelBooking() {
    const cancelRes = await PutCancelBooking(
      {
        tee_time_booking_id: Number(cancelBookingPopup.teetimeToCancelBookingId),
        send_cancellation_email: cancelBookingPopup.send_cancellation_email,
      },
      true,
    );

    if (cancelRes?.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.059")));
      return;
    }

    updateCancelBookingPopup({
      isOpen: false,
      teetimeToCancelDate: "",
      teetimeToCancelTime: "",
      teetimeToCancelBookingId: null,
    });

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.060")));
    props.reloadTeeTimeTabs();
  }

  async function cancelBookingParticipant() {
    const cancelRes = await PutCancelParticipant({ id: cancelPlayerPopup.bookingParticipantId }, true);
    if (cancelRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.061")));
      return;
    }

    updateCancelPlayerPopup({
      isOpen: false,
      bookingParticipantName: "",
      bookingParticipantId: null,
    });

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.125")));
    props.reloadTeeTimeTabs();
  }

  async function handleNoShow(chargeNoShow: boolean) {
    if (!noShowModal.bookingParticipantIds || noShowModal?.bookingParticipantIds?.length === 0) {
      return;
    }

    dispatch(enqueue());
    const putBookingParticipantRes = await Promise.all(
      noShowModal?.bookingParticipantIds?.map(participantId =>
        PutBookingParticipantNoShow(
          {
            id: participantId,
            no_show_type: noShowModal?.type,
            variant_id: noShowChargeModal?.selectedProduct?.id,
            sendEmail: noShowChargeModal?.sendNoShowChargeEmail,
          },
          false,
        ),
      ),
    );
    dispatch(dequeue());

    if (putBookingParticipantRes?.some(res => res?.status !== StatusCode.OK)) {
      const participantError = putBookingParticipantRes?.find(res => res?.status !== StatusCode.OK);
      dispatch(
        showError(
          typeof participantError?.data === "string"
            ? participantError?.data
            : t("elements.tee_sheet.tee_sheet_tabs.064") + valueToString(noShowModal.type),
        ),
      );
      return;
    }

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.065") + valueToString(noShowModal.type)));

    updateNoShowModal({
      isOpen: false,
      type: "charge",
      bookingParticipantIds: [],
      bookingId: null,
      chargeNoShow: false,
    });

    let updatedParticipants = putBookingParticipantRes?.map(res => res?.data);

    //Charge booking participants
    if (chargeNoShow) {
      const updatedParticipantsCharge = await handleNoShowCharge();
      if (updatedParticipantsCharge) {
        updatedParticipants = updatedParticipantsCharge;
      }
    }

    // Manually update the participants' no show values
    void updateParticipants(updatedParticipants);
  }

  function updateTeeTimesWithParticipants(teeTimes: ITeeTimeNew[], participants: Array<IBookingParticipant>) {
    const updatedTeeTimes = cloneDeep(teeTimes);

    participants?.forEach(participant => {
      updatedTeeTimes.forEach(teeTime => {
        const foundParticipantIndex = teeTime?.slots?.findIndex(
          slot => slot.booking_participant_id === participant.id,
        );

        // Manually update the no show values of the matching participant
        if (foundParticipantIndex !== -1) {
          const participantToUpdate = teeTime.slots[foundParticipantIndex];

          teeTime.slots[foundParticipantIndex] = {
            ...participantToUpdate,
            check_in_status: participant?.check_in_status,
            no_show_type: participant?.no_show_type,
            order_financial_status: participant?.order_financial_status,
          };
        }
      });
    });

    return updatedTeeTimes;
  }

  function updateParticipants(updatedParticipants: Array<IBookingParticipant>) {
    if (noShowModal.teeTimeIndex != null) {
      const updatedTeeTimeTabs = updateTeeTimesWithParticipants(props.teetimeTabs, updatedParticipants);

      setTeeTimes(updatedTeeTimeTabs);
      void closeNoShowModal();
    } else {
      props.reloadTeeTimeTabs();
    }
  }

  async function handleNoShowCharge() {
    const manualCardPaymentOption = facilityStore?.paymentOptions?.find(
      paymentOption => paymentOption.payment_method === "manual_card",
    );

    if (
      !noShowChargeModal?.paymentMethod ||
      !noShowChargeModal?.selectedProduct ||
      noShowModal?.bookingParticipantIds?.length === 0 ||
      !manualCardPaymentOption
    ) {
      console.error("No show selection error");
      return null;
    }

    dispatch(enqueue());
    //Create order
    const postOrderRes = await PostOrder({ customer_id: noShowChargeModal?.booking?.customer_id }, false);
    if (postOrderRes?.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.191")));
      dispatch(dequeue());
      return null;
    }

    //Create line item order
    const postLineItemOrderRes = await PostLineItemsToOrder(
      {
        order_id: postOrderRes.data.id,
        variant_id: noShowChargeModal?.selectedProduct?.id,
        quantity: noShowModal?.bookingParticipantIds?.length,
      },
      false,
    );

    if (postLineItemOrderRes?.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.192")));
      dispatch(dequeue());
      return null;
    }

    //Create transaction
    const postTransactionRes = await PostTransaction(
      {
        kind: "sale",
        amount: postLineItemOrderRes.data?.total_price,
        order_id: postOrderRes.data?.id,
        payment_option_id: manualCardPaymentOption?.id,
      },
      false,
    );

    if (postTransactionRes?.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.193")));
      dispatch(dequeue());
      return null;
    }

    if (
      postTransactionRes.data.client_secret &&
      noShowChargeModal?.paymentMethod?.stripe_payment_method_id &&
      postTransactionRes.data.id &&
      postOrderRes.data.id
    ) {
      //Make the card payment
      const participantRes = await confirmCardPaymentNoShow(
        postTransactionRes.data.client_secret,
        String(noShowChargeModal?.paymentMethod?.stripe_payment_method_id),
        postTransactionRes.data.id,
        Number(postOrderRes.data.id),
      );
      if (participantRes) {
        const updatedParticipants: Array<IBookingParticipant> = participantRes?.map(
          res => res?.data as IBookingParticipant,
        );
        dispatch(dequeue());
        return updatedParticipants;
      }
    } else {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.194")));
      dispatch(dequeue());
      return null;
    }

    dispatch(dequeue());
    return null;
  }

  // Confirm Stripe payment intent
  async function confirmCardPaymentNoShow(
    clientSecret: string,
    paymentMethodID: string,
    trans_id: number,
    orderId: number,
  ) {
    if (!stripe) {
      console.error("Stripe hook error");
      return null;
    }
    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: paymentMethodID,
    });

    if (stripeError) {
      dispatch(showError(stripeError.message));
      return null;
    }

    //Capture the payment
    const captureRes = await PutCapture({ payment_intent_id: paymentIntent.id, transaction_id: trans_id }, false);

    if (captureRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.195")));
      return null;
    }

    //Update no show paid value and add order to the participants
    const participantRes = await Promise.all(
      noShowModal?.bookingParticipantIds?.map(participantId =>
        PutBookingParticipant({ id: participantId, no_show_paid: true, order_id: orderId }, false),
      ),
    );

    if (participantRes?.some(res => res?.status !== StatusCode.OK)) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.196")));
    }

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.197")));
    void closeNoShowChargeModal();
    void closeNoShowModal();
    return participantRes;
  }

  function changeSlotBookingInformation(slot: ITeeTimeSlotNew, bookedSlot: ITeeTimeSlotNew = null) {
    // position and tee_time_slot_position can stay the same
    // quantity_remaining will be set at the very end of drag and drop for source and destination
    slot.tee_time_booking_id = bookedSlot?.tee_time_booking_id ?? null;
    slot.tee_time_slot_status = bookedSlot == null ? "available" : bookedSlot?.tee_time_slot_status ?? null;
    slot.tee_time_bookings_holes = bookedSlot?.tee_time_bookings_holes ?? null;
    slot.tee_time_bookings_source = bookedSlot?.tee_time_bookings_source ?? null;
    slot.tee_time_bookings_completed_at = bookedSlot?.tee_time_bookings_completed_at ?? null;
    slot.has_notes = bookedSlot?.has_notes ?? null;
    slot.power_cart_quantity = bookedSlot?.power_cart_quantity ?? null;
    slot.booking_participant_id = bookedSlot?.booking_participant_id ?? null;
    slot.customer_id = bookedSlot?.customer_id ?? null;
    slot.guest = bookedSlot?.guest ?? null;
    slot.tee_time_booking_participant_status = bookedSlot?.tee_time_booking_participant_status ?? null;
    slot.check_in_status = bookedSlot?.check_in_status ?? null;
    slot.turn = bookedSlot?.turn ?? null;
    slot.customer_first_name = bookedSlot?.customer_first_name ?? null;
    slot.customer_last_name = bookedSlot?.customer_last_name ?? null;
    slot.customer_type_title = bookedSlot?.customer_type_title ?? null;
    slot.customer_type_subtitle = bookedSlot?.customer_type_subtitle ?? null;
    slot.customer_type_color = bookedSlot?.customer_type_color ?? null;
    slot.order_financial_status = bookedSlot?.order_financial_status ?? null;
    slot.no_show_type = bookedSlot?.no_show_type ?? null;
    slot.bag_number = bookedSlot?.bag_number ?? null;
  }

  function handleOnDragStart(initial: DragStart) {
    let foundSourceTeeTime: ITeeTimeDraggable = null;

    if (teeSheetView === "side-by-side") {
      for (const division of groupedSideBySideDivisions) {
        foundSourceTeeTime = division.groupedTeeTimes.find(
          teeTime => teeTime.slots[0][0].tee_time_id === Number(initial.source.droppableId),
        );

        if (foundSourceTeeTime) {
          break;
        }
      }
    } else {
      foundSourceTeeTime = state.groupedTeeTimes.find(
        teeTime => teeTime.slots[0][0].tee_time_id === Number(initial.source.droppableId),
      );
    }

    if (!foundSourceTeeTime) {
      return;
    }

    const draggingSlotGroup = foundSourceTeeTime.slots?.find(slotGroup => {
      return slotGroup?.[0]?.tee_time_slot_id === Number(initial.draggableId);
    });

    setDragAndDropState(prev => ({
      ...prev,
      teetimeToolTipDisabled: true,
      draggingSlotGroup: cloneDeep(draggingSlotGroup),
      foundSourceTeeTime,
    }));
  }

  function handleOnDragEnd(result: DropResult) {
    setDragAndDropState(prev => ({ ...prev, teetimeToolTipDisabled: false }));

    if (
      !result.destination ||
      result.source.droppableId === result.destination.droppableId ||
      dragAndDropState.foundSourceTeeTime == null ||
      dragAndDropState.draggingSlotGroup == null ||
      dragAndDropState.draggingSlotGroup.some(
        slot =>
          slot.tee_time_booking_id == null || slot.blocked_type === "crossover" || slot.check_in_status === "checked",
      ) ||
      loadingTeetimeTabActionsContainsId(loadingTeetimeTabActions, dragAndDropState.draggingSlotGroup[0].tee_time_id)
    ) {
      return;
    }

    let foundDestTeeTime: ITeeTimeDraggable = null;

    if (teeSheetView === "side-by-side") {
      for (const division of groupedSideBySideDivisions) {
        foundDestTeeTime = division.groupedTeeTimes.find(
          teeTime => teeTime.slots[0][0].tee_time_id === Number(result.destination.droppableId),
        );

        if (foundDestTeeTime) {
          break;
        }
      }
    } else {
      foundDestTeeTime = state.groupedTeeTimes.find(
        teeTime => teeTime.slots[0][0].tee_time_id === Number(result.destination.droppableId),
      );
    }

    if (!foundDestTeeTime) {
      return;
    }

    const sourceTeeTime = cloneDeep(dragAndDropState.foundSourceTeeTime);
    const destTeeTime = cloneDeep(foundDestTeeTime);

    if (
      loadingTeetimeTabActionsContainsId(loadingTeetimeTabActions, destTeeTime.slots[0][0].tee_time_id) ||
      sourceTeeTime.slots[0][0].course_id !== destTeeTime.slots[0][0].course_id
    ) {
      return;
    }

    let draggingToCrossover = false;

    destTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        if (slot.blocked_type === "crossover") {
          draggingToCrossover = true;
        }
      });
    });

    if (draggingToCrossover) {
      return;
    }

    let moveBookingLength = 0;

    sourceTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        if (slot.tee_time_booking_id === dragAndDropState.draggingSlotGroup[0].tee_time_booking_id) {
          moveBookingLength++;
        }
      });
    });

    const freeSlots: ITeeTimeSlotNew[] = [];

    destTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        if (slot.tee_time_slot_status === "available") {
          freeSlots.push(slot);
        }
      });
    });

    if (moveBookingLength > freeSlots.length) {
      return;
    }

    const moveSlots = take(freeSlots, moveBookingLength);

    let destDraggingSlotToCopyIndex = 0;

    destTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        if (moveSlots.some(moveSlot => moveSlot.tee_time_slot_id === slot.tee_time_slot_id)) {
          changeSlotBookingInformation(slot, dragAndDropState.draggingSlotGroup?.[destDraggingSlotToCopyIndex]);
          destDraggingSlotToCopyIndex++;
        }
      });
    });

    const updatedDestSlots: TDraggableSlot = [];

    destTeeTime.slots.forEach(slotGroup => {
      if (
        slotGroup.length === 1 &&
        slotGroup[0].tee_time_booking_id === dragAndDropState.draggingSlotGroup[0].tee_time_booking_id &&
        updatedDestSlots?.[updatedDestSlots.length - 1]?.[0]?.tee_time_booking_id ===
          dragAndDropState.draggingSlotGroup[0].tee_time_booking_id
      ) {
        updatedDestSlots[updatedDestSlots.length - 1].push(slotGroup[0]);
      } else {
        updatedDestSlots.push(slotGroup);
      }
    });

    destTeeTime.slots = updatedDestSlots;

    const updatedSourceSlots: TDraggableSlot = [];
    const shiftIndexes: number[] = [];

    sourceTeeTime.slots.forEach(slotGroup => {
      if (shiftIndexes.length > 0) {
        if (slotGroup.length === 1 && slotGroup[0].tee_time_slot_status === "available") {
          updatedSourceSlots.push(slotGroup);
          shiftIndexes.push(updatedSourceSlots.length - 1);
        } else if (slotGroup.every(slot => slot.tee_time_slot_status === "booked")) {
          const updatedSlotGroup = cloneDeep(slotGroup);

          const originalUpdateSlotGroup = cloneDeep(updatedSlotGroup);
          const originalSlotGroupLength = updatedSlotGroup.length;

          updatedSlotGroup.forEach(slot => {
            changeSlotBookingInformation(slot);
          });

          const availableSlots = chunk(updatedSlotGroup, 1);

          updatedSourceSlots.push(...availableSlots);
          shiftIndexes.push(...range(updatedSourceSlots.length - availableSlots.length, updatedSourceSlots.length));

          const slotIndexesToGroupAndBook = take(shiftIndexes, originalSlotGroupLength);

          const newSlotGroup: ITeeTimeSlotNew[] = [];

          let sourceDraggingSlotToCopyIndex = 0;

          for (const slotIndex of slotIndexesToGroupAndBook) {
            const slot = cloneDeep(updatedSourceSlots[slotIndex][0]);
            changeSlotBookingInformation(slot, originalUpdateSlotGroup?.[sourceDraggingSlotToCopyIndex]);

            newSlotGroup.push(slot);
            sourceDraggingSlotToCopyIndex++;
          }

          updatedSourceSlots.splice(slotIndexesToGroupAndBook[0], slotIndexesToGroupAndBook.length, newSlotGroup);

          shiftIndexes.splice(0, originalSlotGroupLength);
        }
      } else if (
        slotGroup.some(slot => slot.tee_time_booking_id === dragAndDropState.draggingSlotGroup[0].tee_time_booking_id)
      ) {
        const updatedSlotGroup = cloneDeep(slotGroup);

        updatedSlotGroup.forEach(slot => {
          changeSlotBookingInformation(slot);
        });

        const availableSlots = chunk(updatedSlotGroup, 1);

        updatedSourceSlots.push(...availableSlots);
        shiftIndexes.push(...range(updatedSourceSlots.length - availableSlots.length, updatedSourceSlots.length));
      } else {
        updatedSourceSlots.push(slotGroup);
      }
    });

    sourceTeeTime.slots = updatedSourceSlots;

    let sourceQuantityRemaining = 4;
    let destQuantityRemaining = 4;

    sourceTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        if (slot.tee_time_slot_status === "booked") {
          sourceQuantityRemaining--;
        }
      });
    });

    destTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        if (slot.tee_time_slot_status === "booked") {
          destQuantityRemaining--;
        }
      });
    });

    sourceTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        slot.quantity_remaining = sourceQuantityRemaining;
      });
    });

    destTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        slot.quantity_remaining = destQuantityRemaining;
      });
    });

    const firstDestSlot = destTeeTime.slots[0][0];
    const destDivisions: number[] = JSON.parse(firstDestSlot.divisions_available);
    const draggingBookingHoles: number = dragAndDropState.draggingSlotGroup[0].tee_time_bookings_holes;

    if (!destDivisions.some(destDivision => destDivision === draggingBookingHoles / 9)) {
      dispatch(
        showError(
          `${t("elements.tee_sheet.tee_sheet_tabs.147")} ${draggingBookingHoles} ${t(
            "elements.tee_sheet.tee_sheet_tabs.148",
          )}`,
        ),
      );

      return;
    }

    let nineHoleAvailable = false;
    let eighteenHoleAvailable = false;

    destDivisions?.forEach(division => {
      if (division === 1) {
        nineHoleAvailable = true;
      } else if (division === 2) {
        const leagueId = firstDestSlot.league_id;
        const tournamentId = firstDestSlot.tournament_id;

        if (
          (destDivisions?.length > 1 && firstDestSlot.turn_tee_time_id == null) ||
          firstDestSlot?.turn_tee_time_blocked_type === "blocked" ||
          ((firstDestSlot?.turn_tee_time_league_id || firstDestSlot?.turn_tee_time_tournament_id) &&
            (leagueId !== firstDestSlot?.turn_tee_time_league_id ||
              tournamentId !== firstDestSlot?.turn_tee_time_tournament_id))
        ) {
          nineHoleAvailable = true;
        } else if (firstDestSlot.turn_tee_time_id != null) {
          if (firstDestSlot?.turn_tee_time_quantity_remaining >= moveBookingLength) {
            eighteenHoleAvailable = true;
          } else {
            nineHoleAvailable = true;
          }
        }
      }
    });

    let displayHoleConversionWarning = false;

    if (!nineHoleAvailable && !eighteenHoleAvailable) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.149")));
      return;
    } else if (
      (draggingBookingHoles === 9 && !nineHoleAvailable) ||
      (draggingBookingHoles === 18 && !eighteenHoleAvailable)
    ) {
      displayHoleConversionWarning = true;
    }

    updateMoveTeeTimeBookingConfirmationPopup({
      isOpen: true,
      teeTimeBookingID: dragAndDropState.draggingSlotGroup[0].tee_time_booking_id,
      startTeeTimeID: dragAndDropState.draggingSlotGroup[0].tee_time_id,
      destinationTeeTimeID: firstDestSlot.tee_time_id,
      displayHoleConversionWarning,
      draggingBookingHoles,
    });

    setDragAndDropState(prev => ({
      ...prev,
      draggingSlotGroup: undefined,
      foundSourceTeeTime: undefined,
      moveGroups: [...prev.moveGroups, { sourceTeeTime, destTeeTime }],
    }));
  }

  async function startMoveBookingProcess() {
    const startId = moveTeeTimeBookingConfirmation.startTeeTimeID;
    const destId = moveTeeTimeBookingConfirmation.destinationTeeTimeID;
    const bookingId = moveTeeTimeBookingConfirmation.teeTimeBookingID;

    closeMoveTeeTimeBookingConfirmationPopup();

    await moveBooking(
      {
        type: "move-bookings",
        ids: [startId, destId],
      },
      bookingId,
    );
  }

  function handleCancelTeeTimeMove() {
    const startId = moveTeeTimeBookingConfirmation.startTeeTimeID;
    const destId = moveTeeTimeBookingConfirmation.destinationTeeTimeID;

    setDragAndDropState(prev => {
      return {
        ...prev,
        moveGroups: prev.moveGroups.filter(
          moveGroup =>
            moveGroup.sourceTeeTime?.slots?.[0]?.[0]?.tee_time_id !== startId &&
            moveGroup.destTeeTime?.slots?.[0]?.[0]?.tee_time_id !== destId,
        ),
      };
    });

    closeMoveTeeTimeBookingConfirmationPopup();
  }

  async function moveBooking(moveLoadingTeetimeTabAction: TLoadingMoveBookingsAction, bookingId: number) {
    try {
      setLoadingTeetimeTabActions(prev => {
        return [...prev, moveLoadingTeetimeTabAction];
      });

      const putMove = await PutMoveBooking(
        {
          booking_id: bookingId,
          tee_time_id: moveLoadingTeetimeTabAction.ids[1],
        },
        false,
        teeSheetTabsExitCancelSource.current.token,
      );

      if (teeSheetTabsExitCancelSource.current.token?.reason) {
        return;
      }

      if (putMove.status !== StatusCode.OK) {
        dispatch(
          showError(typeof putMove.data === "string" ? putMove.data : t("elements.tee_sheet.tee_sheet_tabs.150")),
        );

        return;
      }

      const loadTeeTimeTabsStatus = await props.reloadActionTeeTimeTabs(moveLoadingTeetimeTabAction, false);

      if (loadTeeTimeTabsStatus === "failure") {
        return;
      }

      if (moveTeeTimeBookingConfirmation.sendConfirmation) {
        const putBookingConfirmationResponse = await PutBookingConfirmation(
          { booking_group_id: putMove.data.booking_group_id },
          false,
          teeSheetTabsExitCancelSource.current.token,
        );

        if (teeSheetTabsExitCancelSource.current.token?.reason) {
          return;
        }

        if (putBookingConfirmationResponse.status !== StatusCode.OK) {
          dispatch(showError(t("elements.tee_sheet.booking_modal_edit.007")));
          return;
        }
      }

      dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.220")));
    } finally {
      setLoadingTeetimeTabActions(prev =>
        prev.filter(
          prevLoadingTeetimeTabAction =>
            !loadingTeetimeTabActionsAreEqual(prevLoadingTeetimeTabAction, moveLoadingTeetimeTabAction),
        ),
      );

      setDragAndDropState(prev => {
        return {
          ...prev,
          moveGroups: prev.moveGroups.filter(
            moveGroup =>
              moveGroup.sourceTeeTime?.slots?.[0]?.[0]?.tee_time_id !== moveLoadingTeetimeTabAction.ids[0] &&
              moveGroup.destTeeTime?.slots?.[0]?.[0]?.tee_time_id !== moveLoadingTeetimeTabAction.ids[1],
          ),
        };
      });
    }
  }

  // EVENT FUNCTIONS
  async function loadTeeSheetTournaments(ids: Array<number>, token?: CancelToken) {
    if (ids?.length > 0) {
      const tournamentRes = await GetTournament({ ids: ids }, false, token);

      if (token && token.reason) {
        return;
      }

      updateMainState({ teeSheetTournaments: tournamentRes.status === StatusCode.OK ? tournamentRes.data : [] });
    } else {
      updateMainState({ teeSheetTournaments: [] });
    }
  }

  async function loadTeeSheetLeagues(ids: Array<number>, token?: CancelToken) {
    if (ids?.length > 0) {
      const leagueRes = await GetLeague({ ids: ids }, false, token);

      if (token && token.reason) {
        return;
      }

      updateMainState({ teeSheetLeagues: Number(leagueRes.status) == Number(StatusCode.OK) ? leagueRes.data : [] });
    } else {
      updateMainState({ teeSheetLeagues: [] });
    }
  }

  async function removeShotgunTemplate() {
    const params = {
      tee_sheet_id: removeShotgunPopup.teetime.slots[0].tee_sheet_id,
      tournament_id: removeShotgunPopup.teetime.slots[0].tournament_id
        ? removeShotgunPopup.teetime.slots[0].tournament_id
        : null,
      league_id: removeShotgunPopup.teetime.slots[0].league_id ? removeShotgunPopup.teetime.slots[0].league_id : null,
      shotgun_application_id: removeShotgunPopup.teetime.slots[0].shotgun_application_id,
    };

    const removeRes = await RemoveShotgunTemplate(params, true);

    if (Number(removeRes.status) !== Number(StatusCode.OK)) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.057")));
      return;
    }

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.058")));

    closeRemoveShotgunPopup();

    props.reloadTeeTimeTabs();
  }

  async function importDrawsheet(selectedFile: File, customer_id: number) {
    if (!customer_id) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.153")));
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("shotgun_application_id", String(fileImportModal.shotgun_application_id));
    formData.append("customer_id", String(customer_id));

    const importRes = await ImportDrawsheet(formData, true);

    if (importRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.154")));
      closeFileImportModal();
      return;
    }

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.155")));
    closeFileImportModal();
    props.reloadTeeTimeTabs();
  }

  function handleCheckboxChange(event: any) {
    const { id, checked } = event.target;
    updateCancelBookingPopup({
      [id]: checked,
    });
  }

  function handleNoShowCheckboxChange(participantId: number) {
    let updatedSelectedParticipantIds: Array<number> =
      noShowModal?.bookingParticipantIds == null ? [] : [...noShowModal.bookingParticipantIds];

    const participantIndex = updatedSelectedParticipantIds.indexOf(participantId);
    if (participantIndex === -1) {
      updatedSelectedParticipantIds = [...updatedSelectedParticipantIds, participantId];
    } else {
      updatedSelectedParticipantIds = updatedSelectedParticipantIds?.filter(id => id !== participantId);
    }
    updateNoShowModal({ bookingParticipantIds: updatedSelectedParticipantIds });
  }

  async function getNoShowBooking() {
    const noShowCancelToken = cancelToken();
    //Show loader
    updateNoShowChargeModal({ isOpen: true, booking: undefined });
    if (!noShowModal?.bookingId) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.198")));
      void closeNoShowChargeModal();
      return;
    }

    const bookingRes = await GetBooking({ id: noShowModal?.bookingId }, false, noShowCancelToken);
    if (bookingRes?.status !== StatusCode.OK) {
      void closeNoShowChargeModal();
      if (noShowCancelToken?.reason) {
        return;
      }

      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.199")));
      return;
    }
    if (bookingRes?.data?.length > 0) {
      const booking = bookingRes?.data[0];
      if (!booking?.customer_payment_method_id) {
        void closeNoShowChargeModal();
        updateNoCreditCardPopup({ isOpen: true });
        return;
      }
      const noShowProducts = await GetProduct({ type: "No Show Green Fee", extended: true }, false, noShowCancelToken);
      if (noShowProducts?.status !== StatusCode.OK) {
        void closeNoShowChargeModal();
        if (noShowCancelToken?.reason) {
          return;
        }
        dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.200")));
        return;
      }
      //Get the customer payment method information
      void getCustomerPaymentMethod(Number(booking?.customer_payment_method_id), Number(booking?.customer_id));
      updateNoShowChargeModal({ isOpen: true, booking: booking, noShowProducts: noShowProducts?.data });
    } else {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.201")));
      void closeNoShowChargeModal();
      return;
    }
  }

  async function getCustomerPaymentMethod(paymentMethodId: number, customerId: number) {
    const paymentMethodRes = await GetCustomerPaymentMethod({ id: paymentMethodId, customer_id: customerId }, false);

    if (paymentMethodRes?.status !== StatusCode.OK) {
      void closeNoShowChargeModal();
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.202")));
      return;
    }

    updateNoShowChargeModal({ paymentMethod: paymentMethodRes?.data[0] });
  }

  const renderTeeSheetTabsContainer = () => {
    const spinner = (
      <div style={{ height: "24px" }}>
        <Spin />
      </div>
    );

    const errorCallout = <Callout type="error" title={t("elements.tee_sheet.tee_sheet_tabs.161")} />;

    if (teeSheetView === "side-by-side") {
      if (!groupedSideBySideDivisions) {
        return spinner;
      } else if (groupedSideBySideDivisions.length === 0) {
        return errorCallout;
      } else {
        return (
          <DragDropContext onDragStart={handleOnDragStart} onDragEnd={handleOnDragEnd}>
            <div
              className="tee-sheet-tabs-side-by-side-divisions"
              style={{
                gridTemplateColumns: `repeat(${groupedSideBySideDivisions.length}, 1fr)`,
              }}
            >
              {groupedSideBySideDivisions.map(groupedSideBySideDivision => {
                return (
                  <div
                    key={groupedSideBySideDivision.id}
                    className="tee-sheet-tabs-side-by-side-division-header"
                    style={calculateDivisionColors(groupedSideBySideDivision.division?.color)}
                  >
                    {groupedSideBySideDivision.title}
                  </div>
                );
              })}
              {groupedSideBySideDivisions.map(groupedSideBySideDivision => {
                return (
                  <NewTeeSheetTabsDivision
                    key={groupedSideBySideDivision.id}
                    divisionClassName="tee-sheet-tabs-side-by-side-division"
                    teeSheetView={teeSheetView}
                    sortedTeeTimes={groupedSideBySideDivision.sortedTeeTimes}
                    groupedTeeTimes={groupedSideBySideDivision.groupedTeeTimes}
                    teetimeBookingCategories={props.teetimeBookingCategories}
                    dragAndDropState={dragAndDropState}
                    loadingTeetimeTabActions={loadingTeetimeTabActions}
                    teeSheetLeagues={state.teeSheetLeagues}
                    teeSheetTournaments={state.teeSheetTournaments}
                    allDivisions={allDivisions}
                    handleInitiateBooking={handleInitiateBooking}
                    handleEditBooking={handleEditBooking}
                    updateModalState={updateModalState}
                    updateMainState={updateMainState}
                    handleSelectDivisions={handleSelectDivisions}
                  />
                );
              })}
            </div>
          </DragDropContext>
        );
      }
    } else {
      if (!state.groupedTeeTimes) {
        return spinner;
      } else if (state.groupedTeeTimes.length === 0) {
        return errorCallout;
      } else {
        return (
          <DragDropContext onDragStart={handleOnDragStart} onDragEnd={handleOnDragEnd}>
            <NewTeeSheetTabsDivision
              teeSheetView={teeSheetView}
              sortedTeeTimes={state.sortedTeeTimes}
              groupedTeeTimes={state.groupedTeeTimes}
              teetimeBookingCategories={props.teetimeBookingCategories}
              dragAndDropState={dragAndDropState}
              loadingTeetimeTabActions={loadingTeetimeTabActions}
              teeSheetLeagues={state.teeSheetLeagues}
              teeSheetTournaments={state.teeSheetTournaments}
              allDivisions={allDivisions}
              handleInitiateBooking={handleInitiateBooking}
              handleEditBooking={handleEditBooking}
              updateModalState={updateModalState}
              updateMainState={updateMainState}
              handleSelectDivisions={handleSelectDivisions}
            />
          </DragDropContext>
        );
      }
    }
  };

  function getDivisionGroupedTeeTimes(divisionId: number) {
    if (teeSheetView === "side-by-side") {
      return groupedSideBySideDivisions?.find(
        sideBySideTeeSheetDivision => sideBySideTeeSheetDivision.id === divisionId,
      )?.groupedTeeTimes;
    } else {
      return state.groupedTeeTimes;
    }
  }

  async function checkInAllTournamentParticipants() {
    const tournamentId = tournamentCheckInParticipants.teetime?.slots?.[0]?.tournament_id;
    const startTime = tournamentCheckInParticipants.teetime?.slots?.[0]?.start_time;
    const divisionId = tournamentCheckInParticipants.teetime?.slots?.[0]?.division_id;

    const participantIds: number[] = [];

    const groupedTeeTimes: ITeeTimeDraggable[] = getDivisionGroupedTeeTimes(divisionId);

    if (!groupedTeeTimes) {
      return;
    }

    for (let outerIndex = 0; outerIndex < groupedTeeTimes.length; outerIndex++) {
      const teetime = groupedTeeTimes[outerIndex];

      if (
        Number(teetime.slots[0][0].tournament_id) === Number(tournamentId) &&
        String(teetime.slots[0][0].start_time) === startTime
      ) {
        for (let innerIndex = outerIndex; innerIndex < groupedTeeTimes.length; innerIndex++) {
          if (groupedTeeTimes[innerIndex].slots[0][0].tournament_id !== Number(tournamentId)) {
            break;
          }

          const currentTeeTime = groupedTeeTimes[innerIndex];

          currentTeeTime.slots?.forEach(slot => {
            if (slot.length > 1) {
              slot?.forEach(guestSlot => {
                if (guestSlot.booking_participant_id !== null && String(guestSlot.check_in_status) === "unchecked") {
                  participantIds.push(guestSlot.booking_participant_id);
                }
              });
            } else {
              if (slot[0].booking_participant_id !== null && String(slot[0].check_in_status) === "unchecked") {
                participantIds.push(slot[0].booking_participant_id);
              }
            }
          });
        }
      }
    }

    if (participantIds.length === 0) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.170")));
      updateCheckInAllShotgunParticipants({
        isOpen: false,
      });
      return;
    }

    const checkInRes = await PutCheckAllParticipant({ booking_participant_ids: participantIds }, true);

    if (checkInRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.171")));
      updateCheckInAllShotgunParticipants({
        isOpen: false,
      });
      return;
    }

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.172")));
    props.reloadTeeTimeTabs();
  }

  async function checkInAllLeagueParticipants() {
    const leagueId = leagueCheckInParticipants.teetime?.slots?.[0]?.league_id;
    const startTime = leagueCheckInParticipants.teetime?.slots?.[0]?.start_time;
    const divisionId = leagueCheckInParticipants.teetime?.slots?.[0]?.division_id;

    const participantIds: number[] = [];

    const groupedTeeTimes: ITeeTimeDraggable[] = getDivisionGroupedTeeTimes(divisionId);

    if (!groupedTeeTimes) {
      return;
    }

    for (let outerIndex = 0; outerIndex < groupedTeeTimes.length; outerIndex++) {
      const teetime = groupedTeeTimes[outerIndex];

      if (
        Number(teetime.slots[0][0].league_id) === Number(leagueId) &&
        String(teetime.slots[0][0].start_time) === startTime
      ) {
        for (let innerIndex = outerIndex; innerIndex < groupedTeeTimes.length; innerIndex++) {
          if (groupedTeeTimes[innerIndex].slots[0][0].league_id !== Number(leagueId)) {
            break;
          }

          const currentTeeTime = groupedTeeTimes[innerIndex];

          currentTeeTime.slots?.forEach(slot => {
            if (slot.length > 1) {
              slot?.forEach(guestSlot => {
                if (guestSlot.booking_participant_id !== null && String(guestSlot.check_in_status) === "unchecked") {
                  participantIds.push(guestSlot.booking_participant_id);
                }
              });
            } else {
              if (slot[0].booking_participant_id !== null && String(slot[0].check_in_status) === "unchecked") {
                participantIds.push(slot[0].booking_participant_id);
              }
            }
          });
        }
      }
    }

    if (participantIds.length === 0) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.173")));
      updateCheckInAllShotgunParticipants({
        isOpen: false,
      });
      return;
    }

    const checkInRes = await PutCheckAllParticipant({ booking_participant_ids: participantIds }, true);

    if (checkInRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.174")));
      updateCheckInAllShotgunParticipants({
        isOpen: false,
      });
      return;
    }

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.175")));
    props.reloadTeeTimeTabs();
  }

  async function checkInAllShotgunParticipants() {
    const shotgunId = shotgunCheckInParticipants.teetime?.slots?.[0]?.shotgun_application_id;
    const startTime = shotgunCheckInParticipants.teetime?.slots?.[0]?.start_time;
    const divisionId = shotgunCheckInParticipants.teetime?.slots?.[0]?.division_id;

    const participantIds: number[] = [];

    const groupedTeeTimes: ITeeTimeDraggable[] = getDivisionGroupedTeeTimes(divisionId);

    if (!groupedTeeTimes) {
      return;
    }

    groupedTeeTimes?.forEach(teetime => {
      if (Number(teetime.slots[0][0].shotgun_application_id) === Number(shotgunId)) {
        teetime.slots?.forEach(slot => {
          if (slot.length > 1) {
            slot?.forEach(guestSlot => {
              if (
                guestSlot.booking_participant_id !== null &&
                String(guestSlot.check_in_status) === "unchecked" &&
                String(guestSlot.start_time) === startTime
              ) {
                participantIds.push(guestSlot.booking_participant_id);
              }
            });
          } else {
            if (
              slot[0].booking_participant_id !== null &&
              String(slot[0].check_in_status) === "unchecked" &&
              String(slot[0].start_time) === startTime
            ) {
              participantIds.push(slot[0].booking_participant_id);
            }
          }
        });
      }
    });

    if (participantIds.length === 0) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.176")));
      updateCheckInAllShotgunParticipants({
        isOpen: false,
      });
      return;
    }

    const checkInRes = await PutCheckAllParticipant({ booking_participant_ids: participantIds }, true);

    if (checkInRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.177")));
      updateCheckInAllShotgunParticipants({
        isOpen: false,
      });
      return;
    }

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.178")));
    props.reloadTeeTimeTabs();
  }

  // Get the next tee time's start time
  function getNextTeeTimeStartTime(currentTeeTime: ITeeTime) {
    if (!currentTeeTime) {
      return null;
    }

    const teetimes = getDivisionGroupedTeeTimes(currentTeeTime.division_id);

    if (!teetimes) {
      return null;
    }

    const teeTimeIndex = teetimes.findIndex(teetime => teetime.slots[0][0].tee_time_id === currentTeeTime.id);

    if (teeTimeIndex !== -1) {
      return teetimes[teeTimeIndex + 1]?.start_time;
    }

    return null;
  }

  function handleSelectDivisions(teetime: ITeeTimeTabNew) {
    updateSelectDivisionsPopup({ isOpen: true, teetime: teetime });
  }

  async function handlePrintScorecards() {
    let tournamentId = 0;
    let leagueId = 0;
    let params;

    if (selectDivisionsPopup.teetime.slots[0].league_id) {
      leagueId = selectDivisionsPopup.teetime.slots[0].league_id;
      params = {
        league_id: leagueId,
        tee_sheet_id: selectDivisionsPopup.teetime?.slots[0]?.tee_sheet_id,
        facility_id: facilityStore?.facility?.id,
        start_time: selectDivisionsPopup.teetime?.slots[0]?.start_time,
        frontDivision: selectDivisionsPopup.selectedFrontNineDivision,
        backDivision: selectDivisionsPopup.selectedBackNineDivision,
      };
    } else if (selectDivisionsPopup.teetime.slots[0].tournament_id) {
      tournamentId = selectDivisionsPopup.teetime.slots[0].tournament_id;
      params = {
        tournament_id: tournamentId,
        tee_sheet_id: selectDivisionsPopup.teetime.slots[0].tee_sheet_id,
        facility_id: facilityStore?.facility?.id,
        start_time: selectDivisionsPopup.teetime?.slots[0]?.start_time,
        frontDivision: selectDivisionsPopup.selectedFrontNineDivision,
        backDivision: selectDivisionsPopup.selectedBackNineDivision,
      };
    }

    const eventScorecardsRes = await GetEventScorecards(params, true);

    if (eventScorecardsRes.status !== StatusCode.OK) {
      dispatch(showError("Error printing event scorecard")); //TODO: Translate
      return;
    }

    window.open().document.write(String(eventScorecardsRes.data));
  }

  async function addTournamentPlayer(name: string, customer_id: number, slot: ITeeTimeSlotNew) {
    const params = {
      player_name: name,
      organizer_id: customer_id,
      tee_time_slot_id: slot.tee_time_slot_id,
      tournament_id: slot.tournament_id,
    };

    const addPlayerRes = await AddTournamentPlayer(params, true);

    if (addPlayerRes.status !== StatusCode.OK) {
      dispatch(showError("Error adding tournament player"));
      closeAddTournamentPlayer();
      return;
    }

    dispatch(showSuccess("Successfully added player"));
    closeAddTournamentPlayer();
    props.reloadTeeTimeTabs();
  }

  return (
    <>
      {renderTeeSheetTabsContainer()}

      <Portal isMounted={moveTeeTimeBookingConfirmation.isOpen}>
        <Popup
          open={moveTeeTimeBookingConfirmation.isOpen}
          title=""
          description=""
          type="warning"
          onOk={startMoveBookingProcess}
          okText={t("elements.tee_sheet.tee_sheet_tabs.223")}
          cancelText={t("elements.tee_sheet.tee_sheet_tabs.224")}
          onCancel={handleCancelTeeTimeMove}
        >
          <div className="tee-sheet-move-confirmation-heading">{t("elements.tee_sheet.tee_sheet_tabs.221")}</div>
          <div className="tee-sheet-move-confirmation-description">{t("elements.tee_sheet.tee_sheet_tabs.222")}</div>
          <div className="pt-2">
            <Checkbox
              size="small"
              value={moveTeeTimeBookingConfirmation.sendConfirmation}
              checked={moveTeeTimeBookingConfirmation.sendConfirmation}
              onChange={() =>
                updateMoveTeeTimeBookingConfirmationPopup({
                  sendConfirmation: !moveTeeTimeBookingConfirmation.sendConfirmation,
                })
              }
              label={t("elements.tee_sheet.tee_sheet_tabs.225")}
            />
          </div>
          {moveTeeTimeBookingConfirmation.displayHoleConversionWarning && (
            <div className="pt-3">
              <div className="tee-sheet-move-confirmation-heading">{t("elements.tee_sheet.tee_sheet_tabs.163")}</div>
              <div className="tee-sheet-move-confirmation-description">
                {`${t("elements.tee_sheet.tee_sheet_tabs.164")} ${
                  moveTeeTimeBookingConfirmation.draggingBookingHoles
                } ${t("elements.tee_sheet.tee_sheet_tabs.180")} ${
                  moveTeeTimeBookingConfirmation.draggingBookingHoles === 9 ? 18 : 9
                }?`}
              </div>
            </div>
          )}
        </Popup>
      </Portal>
      {/* MODALS */}
      {/* NEW BOOKING MODAL */}
      <BookingModalNew
        cartsAvailable={newBookingModal.cartsAvailable}
        slot={newBookingModal.slot}
        getNextTeeTimeStartTime={getNextTeeTimeStartTime}
        loading={newBookingModal.loading}
        loadingText={newBookingModal.loadingText}
        reloadTeeTimes={props.reloadTeeTimeTabs}
        reloadActionTeeTimeTabs={(addTeetimeTabAction: TLoadingAddBookingsAction) =>
          props.reloadActionTeeTimeTabs(addTeetimeTabAction, true)
        }
      />

      {/* NO SHOW MODAL */}
      <Portal isMounted={noShowModal.isOpen}>
        <Popup
          open={noShowModal.isOpen}
          type="info"
          title={t("elements.tee_sheet.tee_sheet_tabs.203")}
          description={t("elements.tee_sheet.tee_sheet_tabs.119")}
          onOk={() => (noShowModal?.chargeNoShow ? getNoShowBooking() : handleNoShow(false))}
          okText={
            noShowModal?.chargeNoShow
              ? t("elements.tee_sheet.tee_sheet_tabs.204")
              : t("elements.tee_sheet.tee_sheet_tabs.120")
          }
          okDisabled={
            !noShowModal.type || !noShowModal.bookingParticipantIds || noShowModal?.bookingParticipantIds?.length === 0
          }
          cancelText={t("elements.tee_sheet.tee_sheet_tabs.121")}
          onCancel={() =>
            updateNoShowModal({
              isOpen: false,
              type: "charge",
              bookingParticipantIds: [],
              bookingId: null,
              teeTimeIndex: null,
            })
          }
        >
          <Select defaultValue={noShowModal.type} onChange={(value: string) => updateNoShowModal({ type: value })}>
            <Select.Option value={"charge"} name={"Charge"}>
              <span>{t("elements.tee_sheet.tee_sheet_tabs.122")}</span>
            </Select.Option>
            <Select.Option value={"defer"} name={"Defer Charge"}>
              <span>{t("elements.tee_sheet.tee_sheet_tabs.123")}</span>
            </Select.Option>
            <Select.Option value={"weather"} name={"Weather"}>
              <span>{t("elements.tee_sheet.tee_sheet_tabs.124")}</span>
            </Select.Option>
          </Select>
          <br />
          {noShowModal?.slotGroup
            ?.filter((filteredSlot: ITeeTimeSlotNew) => filteredSlot?.check_in_status === "unchecked")
            ?.map((slot: ITeeTimeSlotNew, index: number) => {
              return (
                <div
                  key={index}
                  className="tee-time-no-show-row "
                  onClick={() => handleNoShowCheckboxChange(slot?.booking_participant_id)}
                >
                  <div className="pointer-events-none">
                    <Checkbox
                      size="small"
                      value={noShowModal?.bookingParticipantIds?.includes(slot?.booking_participant_id)}
                      checked={noShowModal?.bookingParticipantIds?.includes(slot?.booking_participant_id)}
                      onChange={() => handleNoShowCheckboxChange(slot?.booking_participant_id)}
                      labelHidden
                    />
                  </div>
                  <p>
                    {slot?.customer_first_name} {slot?.customer_last_name} {slot?.guest ? " - Guest" : ""}
                  </p>
                </div>
              );
            })}
          <br />
          <div className="flex justify-right">
            <Checkbox
              size="small"
              value={noShowModal?.chargeNoShow}
              checked={noShowModal?.chargeNoShow}
              onChange={e => updateNoShowModal({ chargeNoShow: e.target.checked })}
              label={t("elements.tee_sheet.tee_sheet_tabs.205")}
            />
          </div>
        </Popup>
      </Portal>

      <Portal isMounted={noCreditCardPopup.isOpen}>
        <Popup
          open={noCreditCardPopup.isOpen}
          type="info"
          title={t("elements.tee_sheet.tee_sheet_tabs.206")}
          description={t("elements.tee_sheet.tee_sheet_tabs.207")}
          onOk={closeNoCreditCardPopup}
          okText={t("elements.tee_sheet.tee_sheet_tabs.208")}
          stacked
        />
      </Portal>

      {/* CHARGE NO SHOW MODAL */}
      <Portal isMounted={noShowChargeModal?.isOpen}>
        <Sheet
          open={noShowChargeModal?.isOpen}
          size="medium"
          closable
          title={t("elements.tee_sheet.tee_sheet_tabs.209")}
          onCancel={() => {
            closeNoShowChargeModal();
            cancelRequest();
          }}
          onOk={() => updateNoShowChargeModal({ confirmationPopup: true })}
          cancelText={t("elements.tee_sheet.tee_sheet_tabs.121")}
          okText={t("elements.tee_sheet.tee_sheet_tabs.210")}
          stacked
          okDisabled={
            !noShowChargeModal?.paymentMethod ||
            !noShowChargeModal?.selectedProduct ||
            noShowModal?.bookingParticipantIds?.length < 1
              ? true
              : false
          }
          overflow
        >
          {noShowChargeModal?.booking === undefined ? (
            <div style={{ display: "flex", margin: "auto", height: "100%" }}>
              <div className="text-center m-auto">
                <div style={{ height: "32px" }}>
                  <Spin />
                </div>
                <div>Loading...</div>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col">
              {noShowModal?.slotGroup
                ?.filter((filteredSlot: ITeeTimeSlotNew) => filteredSlot?.check_in_status === "unchecked")
                ?.map((slot: ITeeTimeSlotNew, index: number) => {
                  return (
                    <div
                      key={index}
                      className="tee-time-no-show-row "
                      onClick={() => handleNoShowCheckboxChange(slot?.booking_participant_id)}
                    >
                      <div className="pointer-events-none">
                        <Checkbox
                          size="small"
                          value={noShowModal?.bookingParticipantIds?.includes(slot?.booking_participant_id)}
                          checked={noShowModal?.bookingParticipantIds?.includes(slot?.booking_participant_id)}
                          onChange={() => handleNoShowCheckboxChange(slot?.booking_participant_id)}
                          labelHidden
                        />
                      </div>
                      <p>
                        {slot?.customer_first_name} {slot?.customer_last_name} {slot?.guest ? " - Guest" : ""}
                      </p>
                    </div>
                  );
                })}
              <Select
                label={t("secure.facility.tee_sheet.tee_sheet_booking_details.060")}
                onChange={(productId, product) => updateNoShowChargeModal({ selectedProduct: product })}
                defaultValue={noShowChargeModal.selectedProduct?.id}
              >
                {noShowChargeModal?.noShowProducts?.length >= 1 &&
                  noShowChargeModal?.noShowProducts?.map((item: IProduct) => {
                    const productTitle = item?.title;
                    const preferredTitle = item?.preferred_title;
                    return (
                      item.variants &&
                      item.variants
                        .filter(variantFiltered => variantFiltered.price > 0)
                        .map((variant, i) => {
                          return (
                            <Select.Option key={i} value={variant.id} name={variant.title} extraValues={variant}>
                              <div>
                                <span>{preferredTitle ? preferredTitle : productTitle} </span>
                                {productTitle !== variant.title && (
                                  <span className="text-subdued text-sm">{variant.title}</span>
                                )}
                                <span>
                                  {" "}
                                  - <LocaleCurrency currency="cad" amount={variant.price} />
                                </span>
                              </div>
                            </Select.Option>
                          );
                        })
                    );
                  })}
              </Select>

              <div className="mt-auto text-lg">
                <ul>
                  <li className="flex justify-end">
                    <span className="mr-4 text-medium">{noShowChargeModal?.selectedProduct?.title}</span>
                    <LocaleCurrency currency="cad" amount={noShowChargeModal?.selectedProduct?.price ?? 0} />
                  </li>
                  <li className="flex justify-end">
                    <span className="mr-4 text-medium">
                      {t("secure.facility.tee_sheet.tee_sheet_booking_details.063")}
                    </span>
                    <span>{noShowModal?.bookingParticipantIds?.length}</span>
                  </li>
                  <li className="flex justify-end text-bold">
                    <span className="mr-4">{t("secure.facility.tee_sheet.tee_sheet_booking_details.064")}</span>
                    <LocaleCurrency
                      currency="cad"
                      amount={
                        noShowChargeModal?.selectedProduct
                          ? noShowModal?.bookingParticipantIds?.length * noShowChargeModal?.selectedProduct?.price
                          : 0
                      }
                    />
                  </li>
                </ul>
                <div>
                  <Toggle
                    label="Send No Show Charge Email"
                    id="sendNoShowChargeEmail"
                    checked={noShowChargeModal?.sendNoShowChargeEmail}
                    onChange={e => updateNoShowChargeModal({ sendNoShowChargeEmail: e.target.checked })}
                  />
                </div>
              </div>
            </div>
          )}
        </Sheet>
      </Portal>

      {/* NO SHOW CHARGE CONFIRMATION */}
      <Portal isMounted={noShowChargeModal?.confirmationPopup}>
        <Popup
          onCancel={() => updateNoShowChargeModal({ confirmationPopup: false })}
          onOk={() => handleNoShow(true)}
          okText="Charge"
          open={noShowChargeModal?.confirmationPopup}
          type="info"
          title={t("elements.tee_sheet.tee_sheet_tabs.212")}
          stacked
          description={`${t("elements.tee_sheet.tee_sheet_tabs.213")} ${noShowChargeModal?.paymentMethod?.last4} ${t(
            "elements.tee_sheet.tee_sheet_tabs.214",
          )}`}
        />
      </Portal>

      {/* CREATE SHOTGUN MODAL */}
      <Portal isMounted={createShotgunModal.isOpen}>
        <CreateShotgunModal
          open={createShotgunModal.isOpen}
          teetime={createShotgunModal.teetime}
          courses={facilityStore.facility?.courses}
          bookingCategories={bookingCategories}
          templates={shotgunTemplates}
          onOk={() => {
            closeCreateShotgunModal();
            props.reloadTeeTimeTabs();
          }}
          onCancel={closeCreateShotgunModal}
        />
      </Portal>

      {/* AUDIT TRAIL MODAL */}
      <Portal isMounted={auditTrailModal.isOpen}>
        <AuditTrailModal
          open={auditTrailModal.isOpen}
          teetime={auditTrailModal.teetime}
          bookingId={auditTrailModal.bookingId}
          onCancel={() =>
            updateAuditTrailModal({
              isOpen: false,
              teetime: null,
              type: null,
            })
          }
          type={auditTrailModal.type}
        />
      </Portal>

      {/* TEE TIME DETAILS MODAL */}
      <Portal isMounted={teeTimeDetailsModal.isOpen}>
        <TeeTimeDetailsModal
          open={teeTimeDetailsModal.isOpen}
          teetime={teeTimeDetailsModal.teetime}
          bookingCategories={bookingCategories}
          onCancel={closeTeeTimeDetailsModal}
        />
      </Portal>

      {/* ADD TEE TIME MODAL */}
      <Portal isMounted={addTeeTimeModal.isOpen}>
        <AddTeeTimeModal
          open={addTeeTimeModal.isOpen}
          teetime={addTeeTimeModal.teetime}
          onOk={() => {
            closeAddTeeTimeModal();
            props.reloadTeeTimeTabs();
          }}
          onCancel={closeAddTeeTimeModal}
        />
      </Portal>

      {/* ADD SQUEEZE TIME POPUP */}
      <Portal isMounted={addSqueezeTimeModal.isOpen}>
        <Popup
          open={addSqueezeTimeModal.isOpen}
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.159")}
          description={t("elements.tee_sheet.tee_sheet_tabs.162")}
          onOk={createSqueezeTime}
          okText={t("elements.tee_sheet.tee_sheet_tabs.167")}
          onCancel={closeAddSqueezeTimeModal}
          backDropCancel={true}
        />
      </Portal>

      {/* EDIT START HOLE MODAL */}
      <Portal isMounted={editStartHoleModal.isOpen}>
        <EditStartHoleModal
          open={editStartHoleModal.isOpen}
          teetime={editStartHoleModal.teetime}
          onOk={() => {
            closeEditStartHoleModal();
            props.reloadTeeTimeTabs();
          }}
          onCancel={closeEditStartHoleModal}
        />
      </Portal>

      {/* EDIT BOOKING MODAL */}
      <Portal isMounted={editBookingModal.isOpen}>
        <BookingModalEdit
          cartsAvailable={editBookingModal.cartsAvailable}
          selectedBooking={editBookingModal.selectedBooking}
          teeTimeLock={editBookingModal.teeTimeLock}
          refreshTeeTime={true}
          loading={editBookingModal.loading}
          loadingText={editBookingModal.loadingText}
          reloadTeeTimes={props.reloadTeeTimeTabs}
          reloadTeeTime={(updateTeetimeTabAction: TLoadingUpdateBookingAction) =>
            void props.reloadActionTeeTimeTabs(updateTeetimeTabAction, true)
          }
        />
      </Portal>

      {/* POPUPS */}
      {/* BLOCKED TEE TIME POPUP */}
      <Portal isMounted={blockedTeeTimePopup.isOpen}>
        <Popup
          open={blockedTeeTimePopup.isOpen}
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.030")}
          description={t("elements.tee_sheet.tee_sheet_tabs.031")}
          onOk={() => {
            closeBlockedTeeTimePopup();
            void handleInitiateBooking(state.selectedTeeTime);
          }}
          okText={t("elements.tee_sheet.tee_sheet_tabs.032")}
          onCancel={closeBlockedTeeTimePopup}
          backDropCancel={true}
        />
      </Portal>

      {/* SELECT DIVISIONS POPUP */}
      <Portal isMounted={selectDivisionsPopup.isOpen}>
        <Popup
          open={selectDivisionsPopup.isOpen}
          type="info"
          title={"Select Course Divisions"}
          description={t("Select front and back holes for scorecard")}
          onOk={() => {
            closeSelectDivisionsPopup();
            void handlePrintScorecards();
          }}
          okText={"Print"}
          onCancel={closeSelectDivisionsPopup}
          backDropCancel={true}
        >
          <div className="flex gap-5 mt-3">
            <div className="w-full">
              <Select
                label="Front Nine"
                onChange={(value: string) => updateSelectDivisionsPopup({ selectedFrontNineDivision: value })}
              >
                {facilityStore?.facility?.courses[0]?.divisions?.map((division: any, index: number) => {
                  return (
                    <Select.Option value={division.id} name={division.title} key={index}>
                      <span>{division.title}</span>
                    </Select.Option>
                  );
                })}
              </Select>
            </div>

            <div className="w-full">
              <Select
                label="Back Nine"
                onChange={(value: string) => updateSelectDivisionsPopup({ selectedBackNineDivision: value })}
              >
                {facilityStore?.facility?.courses[0]?.divisions?.map((division: any, index: number) => {
                  return (
                    <Select.Option value={division.id} name={division.title} key={index}>
                      <span>{division.title}</span>
                    </Select.Option>
                  );
                })}
              </Select>
            </div>
          </div>
        </Popup>
      </Portal>

      {/* CANCEL BOOKING POPUP */}
      <Portal isMounted={cancelBookingPopup.isOpen}>
        <Popup
          open={cancelBookingPopup.isOpen}
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.086")}
          description={`${t("elements.tee_sheet.tee_sheet_tabs.087")} ${cancelBookingPopup?.teetimeToCancelDate} at ${
            cancelBookingPopup?.teetimeToCancelTime
          }?`}
          onOk={cancelBooking}
          okText={t("elements.tee_sheet.tee_sheet_tabs.088")}
          onCancel={() =>
            updateCancelBookingPopup({
              isOpen: false,
              teetimeToCancelDate: "",
              teetimeToCancelTime: "",
              teetimeToCancelBookingId: null,
            })
          }
          backDropCancel={false}
        >
          <div className="mt-3">
            <Checkbox
              id={"send_cancellation_email"}
              size="small"
              value={cancelBookingPopup.send_cancellation_email}
              checked={cancelBookingPopup.send_cancellation_email}
              onChange={handleCheckboxChange}
              label={t("elements.tee_sheet.tee_sheet_tabs.179")}
            />
          </div>
        </Popup>
      </Portal>

      {/* CANCEL PLAYER POPUP */}
      <Portal isMounted={cancelPlayerPopup.isOpen}>
        <Popup
          open={cancelPlayerPopup.isOpen}
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.089")}
          description={`${t("elements.tee_sheet.tee_sheet_tabs.090")} ${cancelPlayerPopup.bookingParticipantName}?`}
          onOk={cancelBookingParticipant}
          okText={t("elements.tee_sheet.tee_sheet_tabs.091")}
          onCancel={() =>
            updateCancelPlayerPopup({
              isOpen: false,
              bookingParticipantName: "",
              bookingParticipantId: null,
            })
          }
          backDropCancel={false}
        />
      </Portal>

      {/* TEE TIME BOOKED POPUP */}
      <Portal isMounted={teeTimeBookedPopup.isOpen}>
        <Popup
          open={teeTimeBookedPopup.isOpen}
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.016")}
          description={t("elements.tee_sheet.tee_sheet_tabs.017")}
          onOk={closeTeeTimeBookedPopup}
          okText={t("elements.tee_sheet.tee_sheet_tabs.032")}
          onCancel={closeTeeTimeBookedPopup}
          backDropCancel={false}
        />
      </Portal>

      {/* TEE TIME LOCKED POPUP */}
      <Portal isMounted={teeTimeLockedPopup.isOpen}>
        <Popup
          open={teeTimeLockedPopup.isOpen}
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.014")}
          description={t("elements.tee_sheet.tee_sheet_tabs.015")}
          onOk={closeTeeTimeLockedPopup}
          okText={t("elements.tee_sheet.tee_sheet_tabs.032")}
          onCancel={closeTeeTimeLockedPopup}
          backDropCancel={true}
        />
      </Portal>

      {/* REMOVE TEE TIME POPUP */}
      <Portal isMounted={removeTeeTimePopup.isOpen}>
        <Popup
          open={removeTeeTimePopup.isOpen}
          stacked
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.128")}
          description={t("elements.tee_sheet.tee_sheet_tabs.129")}
          onOk={removeTeeTime}
          okText={t("elements.tee_sheet.tee_sheet_tabs.130")}
          onCancel={closeRemoveTeeTimePopup}
          backDropCancel={false}
        />
      </Portal>

      {/* REMOVE SHOTGUN POPUP */}
      <Portal isMounted={removeShotgunPopup.isOpen}>
        <Popup
          open={removeShotgunPopup.isOpen}
          stacked
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.099")}
          description={t("elements.tee_sheet.tee_sheet_tabs.100")}
          onOk={removeShotgunTemplate}
          okText={t("elements.tee_sheet.tee_sheet_tabs.101")}
          onCancel={closeRemoveShotgunPopup}
          backDropCancel={false}
        />
      </Portal>

      <ImportDrawSheetModal
        onCancel={closeFileImportModal}
        onOk={(selectedFile: File, customer_id: number) => {
          void importDrawsheet(selectedFile, customer_id);
        }}
        open={fileImportModal.isOpen}
      />

      <AddTournamentPlayerModal
        onCancel={closeAddTournamentPlayer}
        onOk={(name: string, customer_id: number) => {
          void addTournamentPlayer(name, customer_id, addTournamentPlayerModal?.slot);
        }}
        open={addTournamentPlayerModal?.isOpen}
        organizer={addTournamentPlayerModal?.organizer}
      />

      {/* CHECK IN SHOTGUN PARTICIPANTS */}
      <Portal isMounted={shotgunCheckInParticipants.isOpen}>
        <Popup
          open={shotgunCheckInParticipants.isOpen}
          stacked
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.181")}
          description={t("elements.tee_sheet.tee_sheet_tabs.182")}
          onOk={checkInAllShotgunParticipants}
          okText={t("elements.tee_sheet.tee_sheet_tabs.183")}
          onCancel={closeCheckInAllShotgunParticipants}
          backDropCancel={false}
        />
      </Portal>

      {/* CHECK IN TOURNAMENT PARTICIPANTS */}
      <Portal isMounted={tournamentCheckInParticipants.isOpen}>
        <Popup
          open={tournamentCheckInParticipants.isOpen}
          stacked
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.181")}
          description={t("elements.tee_sheet.tee_sheet_tabs.184")}
          onOk={checkInAllTournamentParticipants}
          okText={t("elements.tee_sheet.tee_sheet_tabs.183")}
          onCancel={closeCheckInAllTournamentParticipants}
          backDropCancel={false}
        />
      </Portal>

      {/* CHECK IN LEAGUE PARTICIPANTS */}
      <Portal isMounted={leagueCheckInParticipants.isOpen}>
        <Popup
          open={leagueCheckInParticipants.isOpen}
          stacked
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.181")}
          description={t("elements.tee_sheet.tee_sheet_tabs.185")}
          onOk={checkInAllLeagueParticipants}
          okText={t("elements.tee_sheet.tee_sheet_tabs.183")}
          onCancel={closeCheckInAllLeagueParticipants}
          backDropCancel={false}
        />
      </Portal>
    </>
  );
}
